/**
 * The offer editor's lifecycle control. It shows whether the offer is a
 * draft, published, or retired, and offers the next step: publish, retire,
 * republish, or make it the main offer. It holds the lifecycle state itself,
 * seeded from the loaded offer, so a status change never resets the form.
 *
 * Aliases: offer status control, publish offer button, retire offer button,
 * make main offer button
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { OfferStatusBadge } from "~/components/offer-status-badge";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import {
  changeOfferStatus,
  makeMainOffer,
  type OfferLifecycleState,
} from "~/lib/functions/offer-lifecycle";
import { OfferStatus, type OfferType } from "~/lib/schemas/db-enums";
import {
  OFFER_ACTION_TARGET_STATUS,
  offerStatusActions,
  type OfferStatusAction,
} from "~/lib/shared/offer-lifecycle";

const ACTIONS = {
  publish: { label: "Publish", success: "Offer published", variant: "primary" },
  republish: {
    label: "Republish",
    success: "Offer published again",
    variant: "primary",
  },
  make_main: {
    label: "Make main offer",
    success: "This is now your main offer",
    variant: "default",
  },
  retire: {
    label: "Retire",
    success: "Offer retired",
    variant: "destructive-ghost",
  },
} as const satisfies Record<
  OfferStatusAction,
  {
    label: string;
    success: string;
    variant: "primary" | "default" | "destructive-ghost";
  }
>;

// Everything that lists offers or carries the coach's main offer.
const OFFER_QUERY_KEYS = [
  ["coach"],
  ["coach-offers"],
  ["coach-mini-sprints"],
  ["my-coaching-profile"],
] as const;

function describeStatus(status: OfferStatus, isMain: boolean): string {
  switch (status) {
    case OfferStatus.Draft:
      return "Only you can see this offer. Publish it when it's ready for clients.";
    case OfferStatus.Published:
      return isMain
        ? "Your main offer, featured on your profile."
        : "Clients can see this offer. Retiring hides it without deleting anything attached to it.";
    case OfferStatus.Retired:
      return "Hidden from clients and not for sale. Past purchases stay as they are.";
  }
}

export function OfferStatusControl({
  offerId,
  offerType,
  initialStatus,
  initialIsMain,
  hasUnsavedChanges,
}: {
  offerId: string;
  offerType: OfferType;
  initialStatus: OfferStatus;
  initialIsMain: boolean;
  hasUnsavedChanges: boolean;
}) {
  const queryClient = useQueryClient();
  const [lifecycle, setLifecycle] = useState({
    status: initialStatus,
    isMain: initialIsMain,
  });

  const mutation = useMutation({
    mutationFn: (action: OfferStatusAction): Promise<OfferLifecycleState> =>
      action === "make_main"
        ? makeMainOffer({ data: { offerId } })
        : changeOfferStatus({
            data: { offerId, to: OFFER_ACTION_TARGET_STATUS[action] },
          }),
    onSuccess: (next, action) => {
      setLifecycle({
        status: next.status,
        isMain: next.mainOfferId === offerId,
      });
      toast.success(ACTIONS[action].success);
      for (const queryKey of OFFER_QUERY_KEYS) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: (error) => {
      toast.error(error.message || "Couldn't update this offer");
    },
  });

  const actions = offerStatusActions({ ...lifecycle, type: offerType });

  return (
    <Card className="gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <OfferStatusBadge status={lifecycle.status} />
          {lifecycle.isMain && <Badge variant="secondary">Main</Badge>}
        </div>
        <p className="text-sm text-muted-foreground">
          {describeStatus(lifecycle.status, lifecycle.isMain)}
        </p>
        {hasUnsavedChanges && (
          <p className="text-sm text-amber-700">
            Save your changes before you change the offer's status.
          </p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        {actions.map((action) => (
          <Button
            key={action}
            type="button"
            variant={ACTIONS[action].variant}
            disabled={hasUnsavedChanges || mutation.isPending}
            onClick={() => mutation.mutate(action)}
          >
            {ACTIONS[action].label}
          </Button>
        ))}
      </div>
    </Card>
  );
}
